// A bank statement brought into Banking (gap #11): what a chosen file is (a CSV, an OFX, or something
// else said plainly, QA-809), the columns a CSV's first row names and a first guess at which is which,
// and the columns bank.importStatement is told. Kept apart from the screens so they can be tested
// (banking-entry.test.ts) and shared by Riverstone's BankingEntry.tsx and Blueprint's BankingPage.tsx.

const GET_IT = 'Download the CSV or OFX from your bank’s website and choose that.';

/**
 * What a chosen statement file is: a CSV, an OFX, or something else said plainly, so a PDF never
 * reaches the column pickers (QA-809). Read by its content first, then its name.
 */
export function statementKind(name: string, text: string): { format: 'csv' | 'ofx' } | { wrong: string } {
  const head = text.slice(0, 2000);
  if (head.startsWith('%PDF-') || /\.pdf$/i.test(name)) return { wrong: `This is a PDF. ${GET_IT}` };
  if (head.startsWith('PK\u0003\u0004') || /\.(xlsx?|numbers|ods)$/i.test(name))
    return {
      wrong: 'This is a spreadsheet. Save it as CSV, or download the CSV or OFX from your bank’s website.',
    };
  if (/\.(ofx|qfx)$/i.test(name) || /<OFX>/i.test(head)) return { format: 'ofx' };
  // a NUL in the first lines: an image or another file that isn't text
  if (head.includes('\u0000')) return { wrong: `This isn’t a CSV or OFX file. ${GET_IT}` };
  return { format: 'csv' };
}

/** The columns a CSV's first row names, so the person can say which is which. */
export function csvHeader(text: string): string[] {
  const first = text.split(/\r?\n/).find((l) => l.trim()) ?? '';
  const out: string[] = [];
  let cell = '';
  let quoted = false;
  for (const ch of first) {
    if (ch === '"') quoted = !quoted;
    else if (ch === ',' && !quoted) {
      out.push(cell.trim());
      cell = '';
    } else cell += ch;
  }
  out.push(cell.trim());
  return out.filter(Boolean);
}

/** A best first guess at which column is which, from the names banks usually give them. */
export function guessColumns(cols: string[]) {
  const find = (re: RegExp) => cols.find((c) => re.test(c)) ?? '';
  return {
    date: find(/date|posted/i),
    description: find(/desc|memo|payee|details|narrative/i),
    amount: find(/^amount$|^amt$|amount \(|transaction amount/i),
    debit: find(/debit|withdrawal|money out|paid out/i),
    credit: find(/credit|deposit|money in|paid in/i),
    reference: find(/ref|cheque|check|fitid|id$/i),
  };
}

export type Columns = ReturnType<typeof guessColumns>;

/** What bank.importStatement is told of a CSV's columns: one amount, or money out and in apart. */
export function csvColumns(map: Columns, split: boolean) {
  return {
    date: map.date,
    description: map.description,
    ...(split
      ? {
          ...(map.debit ? { debit: map.debit } : {}),
          ...(map.credit ? { credit: map.credit } : {}),
        }
      : { amount: map.amount }),
    ...(map.reference ? { reference: map.reference } : {}),
  };
}

/** Whether a CSV's columns are enough to bring it in: a date, a description, and the money. */
export const csvReady = (map: Columns | null, split: boolean): boolean =>
  !!map && !!map.date && !!map.description && (split ? !!(map.debit || map.credit) : !!map.amount);
